import { Container, Title, Text, Stack, Modal, Badge } from "@mantine/core";
import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import {
  useCorrectionsAdmin,
  useCreateCorrection,
  useUpdateCorrection,
  useToggleCorrectionActive,
} from "../../../hooks/admin/useCorrections";
import AdminDataTable, {
  AdminColumn,
} from "../../../components/admin/shared/AdminDataTable";
import CorrectionForm, {
  CorrectionFormValues,
} from "../../../components/admin/corrections/CorrectionForm";
import { Correction } from "../../../services/admin/corrections.service";

export default function CorrectionsAdminPage() {
  const [opened, { open, close }] = useDisclosure(false);
  const [editingCorrection, setEditingCorrection] = useState<Correction | null>(
    null
  );

  const { data: corrections = [], isLoading } = useCorrectionsAdmin();
  const createCorrection = useCreateCorrection();
  const updateCorrection = useUpdateCorrection();
  const toggleCorrectionActive = useToggleCorrectionActive();

  const handleAdd = () => {
    setEditingCorrection(null);
    open();
  };

  const handleEdit = (item: Correction) => {
    setEditingCorrection(item);
    open();
  };

  const handleSubmit = async (values: CorrectionFormValues) => {
    if (editingCorrection) {
      await updateCorrection.mutateAsync({
        id: editingCorrection.id,
        ...values,
      });
    } else {
      await createCorrection.mutateAsync(values);
    }
    close();
  };

  const handleToggleActive = async (item: Correction) => {
    await toggleCorrectionActive.mutateAsync({
      id: item.id,
      isActive: !item.isActive,
    });
  };

  const columns: AdminColumn<Correction>[] = [
    {
      key: "correction",
      label: "Correction",
      sortable: true,
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      render: (item) => (
        <Text size="sm" lineClamp={2}>
          {item.description || "—"}
        </Text>
      ),
    },
    {
      key: "isActive",
      label: "Status",
      width: "100px",
      render: (item) => (
        <Badge color={item.isActive ? "green" : "red"} variant="light">
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Corrections</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Manage case corrections
          </Text>
        </div>

        <AdminDataTable
          data={corrections}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onToggleActive={handleToggleActive}
          getId={(item) => item.id}
          getIsActive={(item) => item.isActive}
          searchPlaceholder="Search corrections..."
          emptyMessage="No corrections found"
        />
      </Stack>

      <Modal
        opened={opened}
        onClose={close}
        title={editingCorrection ? "Edit Correction" : "Add New Correction"}
        size="md"
        closeOnClickOutside={false}
      >
        <CorrectionForm
          initialValues={editingCorrection || undefined}
          onSubmit={handleSubmit}
          onCancel={close}
          submitLabel={editingCorrection ? "Update" : "Create"}
          isSubmitting={
            createCorrection.isPending || updateCorrection.isPending
          }
        />
      </Modal>
    </Container>
  );
}
